import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { fill } from '../../utils/template';
import { FormCard } from '../form/FormShell';
import { Field, FieldGrid } from '../form/Field';
import { DiscountErrors, DiscountStrings } from './discountDraft';

/** Порядок показа Пн…Вс, значения — формат бэка: 0 (вс) … 6 (сб). */
const WEEKDAYS: Array<{ value: number; key: keyof DiscountStrings }> = [
  { value: 1, key: 'dayMon' },
  { value: 2, key: 'dayTue' },
  { value: 3, key: 'dayWed' },
  { value: 4, key: 'dayThu' },
  { value: 5, key: 'dayFri' },
  { value: 6, key: 'daySat' },
  { value: 0, key: 'daySun' },
];

const PRESETS: Array<{ key: keyof DiscountStrings; days: number[]; ghost?: boolean }> = [
  { key: 'daysWeekdays', days: [1, 2, 3, 4, 5] },
  { key: 'daysWeekend', days: [6, 0] },
  { key: 'daysAll', days: [1, 2, 3, 4, 5, 6, 0] },
  { key: 'daysClear', days: [], ghost: true },
];

interface ScheduleCardsProps {
  timeStart: string;
  timeEnd: string;
  weekdays: number[];
  startsAt: string;
  endsAt: string;
  errors: DiscountErrors;
  disabled: boolean;
  onTimeStartChange: (value: string) => void;
  onTimeEndChange: (value: string) => void;
  onWeekdayToggle: (weekday: number) => void;
  onWeekdaysSet: (weekdays: number[]) => void;
  onStartsAtChange: (value: string) => void;
  onEndsAtChange: (value: string) => void;
}

/** Расписание: окно внутри суток и дни недели, затем календарный срок. */
export function ScheduleCards({
  timeStart,
  timeEnd,
  weekdays,
  startsAt,
  endsAt,
  errors,
  disabled,
  onTimeStartChange,
  onTimeEndChange,
  onWeekdayToggle,
  onWeekdaysSet,
  onStartsAtChange,
  onEndsAtChange,
}: ScheduleCardsProps) {
  const { t } = useLanguage();
  const strings = t.discounts;

  // Окно вида 23:00 → 02:00. weekdays на бэке — дни начала: «по средам с 23:00
  // до 02:00» действует в среду ночью и до 02:00 четверга. Переход через
  // полночь разрешён, но показан явно: так намеренное «до двух ночи» видно,
  // а опечатка ловится глазом.
  const isOvernight = !!timeStart && !!timeEnd && timeStart > timeEnd;
  const overnightHint = isOvernight ? fill(strings.timeOvernight, { time: timeEnd }) : undefined;

  return (
    <>
      <FormCard title={strings.cardWhen} hint={strings.cardWhenHint}>
        <FieldGrid>
          <Field label={strings.timeFrom} required htmlFor="discount-time-start" error={errors.time}>
            <input
              id="discount-time-start"
              type="time"
              value={timeStart}
              onChange={(event) => onTimeStartChange(event.target.value)}
              className="qp-control qp-control--num"
              disabled={disabled}
            />
          </Field>

          <Field label={strings.timeTo} required htmlFor="discount-time-end" hint={overnightHint}>
            <input
              id="discount-time-end"
              type="time"
              value={timeEnd}
              onChange={(event) => onTimeEndChange(event.target.value)}
              className="qp-control qp-control--num"
              disabled={disabled}
            />
          </Field>
        </FieldGrid>

        <div className="qp-form-group" style={{ marginTop: 18 }}>
          <span className="qp-field-label" id="discount-days-label">
            {strings.days}
          </span>
          <div className="qp-chips">
            {PRESETS.map((preset) => (
              <button
                key={preset.key}
                type="button"
                className={`qp-btn qp-btn--sm ${preset.ghost ? 'qp-btn--ghost' : 'qp-btn--outline'}`}
                onClick={() => onWeekdaysSet(preset.days)}
                disabled={disabled}
              >
                {strings[preset.key]}
              </button>
            ))}
          </div>
          <div className="qp-chips" role="group" aria-labelledby="discount-days-label">
            {WEEKDAYS.map((day) => (
              <button
                key={day.value}
                type="button"
                className="qp-chip qp-chip--day"
                aria-pressed={weekdays.includes(day.value)}
                onClick={() => onWeekdayToggle(day.value)}
                disabled={disabled}
              >
                {strings[day.key]}
              </button>
            ))}
          </div>
          {errors.weekdays && (
            <span className="qp-field-error" role="alert">
              {errors.weekdays}
            </span>
          )}
        </div>
      </FormCard>

      <FormCard title={strings.cardPeriod} hint={strings.cardPeriodHint}>
        <FieldGrid>
          <Field
            label={strings.startsAt}
            htmlFor="discount-starts-at"
            error={errors.starts_at}
            hint={strings.startsAtHint}
          >
            <input
              id="discount-starts-at"
              type="date"
              value={startsAt}
              onChange={(event) => onStartsAtChange(event.target.value)}
              className="qp-control qp-control--num"
              disabled={disabled}
            />
          </Field>

          <Field label={strings.endsAt} htmlFor="discount-ends-at" error={errors.ends_at} hint={strings.endsAtHint}>
            <input
              id="discount-ends-at"
              type="date"
              value={endsAt}
              min={startsAt || undefined}
              onChange={(event) => onEndsAtChange(event.target.value)}
              className="qp-control qp-control--num"
              disabled={disabled}
            />
          </Field>
        </FieldGrid>
      </FormCard>
    </>
  );
}
